import React, { useState } from 'react';
import { guidanceTopicsData, IconMap } from './data';
import { ChevronRight, Sparkles } from 'lucide-react';

const GuidanceSection: React.FC = () => {
    const [activeIndex, setActiveIndex] = useState(0);

    // Color themes for each topic
    const topicThemes = [
        { gradient: 'from-purple-500 to-indigo-500', bg: 'bg-purple-500/10', border: 'border-purple-400/30', glow: 'shadow-purple-500/20' },
        { gradient: 'from-pink-500 to-rose-500', bg: 'bg-pink-500/10', border: 'border-pink-400/30', glow: 'shadow-pink-500/20' },
        { gradient: 'from-blue-500 to-cyan-500', bg: 'bg-blue-500/10', border: 'border-blue-400/30', glow: 'shadow-blue-500/20' },
        { gradient: 'from-emerald-500 to-teal-500', bg: 'bg-emerald-500/10', border: 'border-emerald-400/30', glow: 'shadow-emerald-500/20' },
    ];

    const activeTopic = guidanceTopicsData[activeIndex];
    const activeTheme = topicThemes[activeIndex];
    const ActiveIcon = IconMap[activeTopic.iconName];

    return (
        <section className="py-16 md:py-24 relative z-10">
            <div className="container mx-auto px-4">
                {/* Header */}
                <div className="text-center mb-10 md:mb-14">
                    <h2 className="text-3xl md:text-4xl lg:text-6xl font-serif font-black mb-4 md:mb-6 text-white leading-tight">
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400">Life-Changing Guidance</span> for Every Path
                    </h2>
                    <p className="text-base md:text-lg text-primary-200/80 max-w-2xl mx-auto font-sans leading-relaxed px-2 md:px-0">
                        Get personalized cosmic insights for career, relationships, health, and spiritual growth from ancient wisdom traditions.
                    </p>
                </div>

                {/* Interactive Topic Selector */}
                <div className="max-w-4xl mx-auto">
                    {/* Topic Tabs */}
                    <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-8 md:mb-10">
                        {guidanceTopicsData.map((topic, i) => {
                            const Icon = IconMap[topic.iconName];
                            const theme = topicThemes[i];
                            const isActive = activeIndex === i;

                            return (
                                <button
                                    key={i}
                                    onClick={() => setActiveIndex(i)}
                                    className={`
                                        flex items-center gap-2 px-4 py-2.5 md:px-5 md:py-3 rounded-full font-semibold text-sm md:text-base
                                        transition-all duration-300 ease-out
                                        ${isActive
                                            ? `bg-gradient-to-r ${theme.gradient} text-white shadow-lg ${theme.glow} scale-105`
                                            : 'bg-white/[0.06] text-purple-200/80 hover:bg-white/[0.12] hover:text-white border border-white/10'
                                        }
                                    `}
                                >
                                    {Icon && <Icon className="w-4 h-4 md:w-5 md:h-5" />}
                                    <span>{topic.title}</span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Active Topic Content */}
                    <div
                        className={`
                            relative overflow-hidden rounded-3xl border ${activeTheme.border} ${activeTheme.bg}
                            backdrop-blur-sm p-6 md:p-10 transition-all duration-500
                        `}
                    >
                        {/* Decorative gradient bar */}
                        <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${activeTheme.gradient}`} />

                        {/* Floating sparkle decoration */}
                        <div className="absolute top-4 right-4 text-white/10">
                            <Sparkles className="w-20 h-20 md:w-32 md:h-32" />
                        </div>

                        <div className="relative z-10">
                            {/* Topic Header */}
                            <div className="flex items-center gap-4 mb-6 md:mb-8">
                                <div className={`w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br ${activeTheme.gradient} flex items-center justify-center shadow-lg`}>
                                    {ActiveIcon && <ActiveIcon className="w-7 h-7 md:w-8 md:h-8 text-white" />}
                                </div>
                                <div>
                                    <h3 className="text-2xl md:text-3xl font-bold text-white font-sans">
                                        {activeTopic.title}
                                    </h3>
                                    <p className="text-purple-200/70 text-sm md:text-base">
                                        Common questions we can answer
                                    </p>
                                </div>
                            </div>

                            {/* Questions List */}
                            <div className="grid gap-3 md:gap-4">
                                {activeTopic.questions.map((question, j) => (
                                    <div
                                        key={j}
                                        className="group flex items-center gap-3 md:gap-4 p-4 md:p-5 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/5 hover:border-white/15 transition-all duration-200 cursor-pointer"
                                        style={{
                                            animationDelay: `${j * 100}ms`,
                                        }}
                                    >
                                        <div className={`flex-shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-br ${activeTheme.gradient} flex items-center justify-center text-white font-bold text-sm md:text-base shadow-md`}>
                                            {j + 1}
                                        </div>
                                        <span className="flex-1 text-white/90 text-base md:text-lg font-medium">
                                            {question}
                                        </span>
                                        <ChevronRight className="w-5 h-5 text-white/30 group-hover:text-white/60 group-hover:translate-x-1 transition-all" />
                                    </div>
                                ))}
                            </div>

                            {/* CTA hint */}
                            <p className="mt-6 md:mt-8 text-center text-purple-200/60 text-sm">
                                ✨ Start a conversation to get personalized answers
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default GuidanceSection;
